// src/pages/InfraDetailPage.jsx
import React, { useState, useEffect, useRef } from "react";
import { useLocale } from "../contexts/LocaleContext";
import { getInfraDetailData } from "../api/apiService";
import PageBanner from "../components/common/PageBanner";
import InfraItemCard from "../components/infra/InfraItemCard";
import { useLocation, useNavigate } from "react-router-dom";

function InfraDetailPage() {
  const { locale } = useLocale();
  const [infraPageData, setInfraPageData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const location = useLocation();
  const navigate = useNavigate();
  const categoryRefs = useRef({});

  useEffect(() => {
    const fetchPageData = async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await getInfraDetailData(locale);
        setInfraPageData(data);
      } catch (err) {
        setError(err.message || "Failed to load content.");
        setInfraPageData(null);
      } finally {
        setLoading(false);
      }
    };
    fetchPageData();
  }, [locale]);

  useEffect(() => {
    if (!loading && infraPageData && location.hash) {
      const idToScrollTo = location.hash.substring(1);
      const element = document.getElementById(idToScrollTo);
      if (element) {
        element.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    }
  }, [loading, infraPageData, location.hash, navigate]);

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-[calc(100vh-200px)]">
        <p className="text-xl text-gray-500">Loading...</p>
      </div>
    );
  }
  if (error) {
    return (
      <div className="container mx-auto p-4 text-center">
        <p className="text-red-500 text-xl">Error: {error}</p>
      </div>
    );
  }
  if (!infraPageData) {
    const message = error
      ? error
      : `infraPageData not found or content is unavailable.`;
    return (
      <div className="container mx-auto p-4 text-center">
        <p className="text-gray-700 text-xl">{message}</p>
      </div>
    );
  }

  const { banner, infra_category } = infraPageData;

  return (
    <div className="bg-gray-100 min-h-screen">
      {" "}
      <PageBanner
        title={banner?.title}
        imageUrl={banner?.image?.url}
        titleColorIsWhite={banner?.titleColorIsWhite ?? true}
        bannerHeightClass="h-52 sm:h-60 md:h-64 lg:h-72"
      />
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-10 md:py-16">
        {infra_category && infra_category.length > 0 ? (
          infra_category.map((category) => (
            <div
              key={category.id}
              className="mb-12 md:mb-16 scroll-mt-20"
              id={category.name}
              ref={(el) => (categoryRefs.current[category.name] = el)}
            >
              {/* Category Title and Subtitle Section */}
              <div className="bg-[#003366] text-white p-6 md:p-8 rounded-t-lg shadow-md">
                <h2 className="text-2xl md:text-3xl font-bold mb-2 text-center">
                  {category.title}
                </h2>
                {category.subtitle && (
                  <p className="text-sm md:text-base text-gray-200 leading-relaxed text-center whitespace-pre-line">
                    {category.subtitle}
                  </p>
                )}
              </div>

              {/* Infra Items for this category */}
              {category.infra_items && category.infra_items.length > 0 ? (
                <div className="bg-white p-4 sm:p-6 lg:p-8 rounded-b-lg shadow-md">
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 md:gap-8">
                    {category.infra_items.map((item) => (
                      <InfraItemCard key={item.id} item={item} />
                    ))}
                  </div>
                </div>
              ) : (
                <div className="bg-white p-6 rounded-b-lg shadow-md text-center text-gray-500">
                  No items in this category.
                </div>
              )}
            </div>
          ))
        ) : (
          <p className="text-center text-gray-600 text-lg">
            No infra-categories found.
          </p>
        )}
      </div>
    </div>
  );
}

export default InfraDetailPage;
